import { SeededRandom } from '../core/Random';
import type { OwnedUnit, Rarity, SaveData } from '../core/types';
import { banners, units } from '../data/content';
import { WalletService } from './WalletService';

export interface SummonResult { unitId: string; rarity: Rarity; duplicate: boolean; shards: number }

const baseRates: Array<{ value: Rarity; weight: number }> = [
  { value: 'Common', weight: 48 }, { value: 'Rare', weight: 30 }, { value: 'Epic', weight: 14 },
  { value: 'Legendary', weight: 6 }, { value: 'Mythic', weight: 1.8 }, { value: 'Secret', weight: 0.2 },
];

const shardByRarity: Record<Rarity, number> = { Common: 5, Rare: 8, Epic: 15, Legendary: 30, Mythic: 60, Secret: 100, Limited: 70 };

export class SummonService {
  constructor(private readonly save: SaveData, private readonly rng = new SeededRandom()) {}

  summon(bannerId: string, count: 1 | 10): SummonResult[] {
    const banner = banners.find((item) => item.id === bannerId);
    if (!banner) throw new Error('Unknown banner');
    if (bannerId === 'beginner' && this.save.summon.beginnerPulls + count > 50) throw new Error('Beginner banner limit reached');
    const total = banner.cost * count;
    const wallet = new WalletService(this.save.wallet);
    wallet.spend({ [banner.currency]: total });
    const results: SummonResult[] = [];
    for (let i = 0; i < count; i += 1) results.push(this.pullOne(bannerId, banner.featured));
    return results;
  }

  private pullOne(bannerId: string, featured: string | null): SummonResult {
    if (bannerId === 'beginner') this.save.summon.beginnerPulls += 1;
    const pityKey = bannerId === 'featured' || bannerId === 'event' ? 'featuredPity' : 'standardPity';
    this.save.summon[pityKey] += 1;
    let rarity: Rarity;
    if ((bannerId === 'featured' || bannerId === 'event') && this.save.summon.featuredPity >= 80) rarity = 'Mythic';
    else if (bannerId === 'beginner' && this.save.summon.beginnerPulls === 30) rarity = this.rng.next() < 0.15 ? 'Mythic' : 'Legendary';
    else if (bannerId === 'secret') rarity = this.rng.next() < 0.05 ? 'Secret' : this.rng.weighted([{ value: 'Rare' as Rarity, weight: 55 }, { value: 'Epic' as Rarity, weight: 35 }, { value: 'Legendary' as Rarity, weight: 10 }]);
    else {
      const rates = baseRates.map((rate) => ({ ...rate }));
      if (bannerId === 'featured' && this.save.summon.featuredPity >= 60) {
        const mythic = rates.find((rate) => rate.value === 'Mythic')!;
        mythic.weight += (this.save.summon.featuredPity - 59) * 4.8;
      }
      rarity = this.rng.weighted(rates);
    }
    let pool = units.filter((unit) => unit.rarity === rarity && unit.rarity !== 'Limited');
    if (bannerId === 'element' && featured) { const featuredUnit = units.find((unit) => unit.id === featured); if (featuredUnit) pool = pool.filter((unit) => unit.element === featuredUnit.element); }
    if (pool.length === 0) pool = units.filter((unit) => unit.rarity === 'Mythic');
    let chosen = this.rng.pick(pool);
    if ((bannerId === 'featured' || bannerId === 'event') && rarity === 'Mythic' && featured) {
      if (this.save.summon.featuredGuarantee || this.rng.next() < 0.5) {
        chosen = units.find((unit) => unit.id === featured) ?? chosen;
        this.save.summon.featuredGuarantee = false;
      } else this.save.summon.featuredGuarantee = true;
      this.save.summon.featuredPity = 0;
    } else if (rarity === 'Mythic' || rarity === 'Secret') this.save.summon[pityKey] = 0;
    rarity = chosen.rarity;
    const owned = this.save.units.find((unit) => unit.definitionId === chosen.id);
    const shards = owned ? shardByRarity[rarity] : 0;
    if (owned) owned.shards += shards;
    else this.save.units.push(this.makeOwned(chosen.id));
    this.save.summon.history.unshift({ banner: bannerId, unitId: chosen.id, rarity, at: Date.now() });
    this.save.summon.history = this.save.summon.history.slice(0, 100);
    return { unitId: chosen.id, rarity, duplicate: Boolean(owned), shards };
  }

  private makeOwned(definitionId: string): OwnedUnit {
    return { instanceId: crypto.randomUUID(), definitionId, level: 1, exp: 0, ascension: 0, awakening: 0, evolved: false, shards: 0, trait: { id: 'fierce-normal', name: 'Fierce', tier: 'Normal', stat: 'attack', value: 0.04 }, locked: true, acquiredAt: Date.now(), relicSlots: {} };
  }
}
